//react-app\src\utils\exportUtils.js
/**
 * SVG export utilities for Laser Cut Interlocking components
 * Modified to export both horizontal and vertical contours to a single SVG file per index
 */

/**
 * Combined SVG export function that exports both horizontal and vertical contours
 * to a single file named shape_X.svg
 * 
 * @param {Array} horizontalContours - Array of horizontal contour groups
 * @param {Array} verticalContours - Array of vertical contour groups
 * @param {Object} modelDimensions - Object with width, height, and depth properties
 * @param {Number} materialThickness - Material thickness in mm
 */
export const generateSVG = (horizontalContours, verticalContours, modelDimensions, materialThickness) => {
  console.log("Generating combined SVG files...");
  console.log("Horizontal contours:", horizontalContours);
  console.log("Vertical contours:", verticalContours);
  
  // Get the maximum number of indices from both contour sets
  const horizontalKeys = Object.keys(horizontalContours)
    .filter(key => !isNaN(parseInt(key)));
    
  const verticalKeys = Object.keys(verticalContours)
    .filter(key => !isNaN(parseInt(key)));
    
  const maxIndex = Math.max(
    horizontalKeys.length > 0 ? Math.max(...horizontalKeys.map(k => parseInt(k))) + 1 : 0,
    verticalKeys.length > 0 ? Math.max(...verticalKeys.map(k => parseInt(k))) + 1 : 0
  );
  
  console.log(`Exporting ${maxIndex} SVG files`);
  
  // Create SVGs for each index, combining horizontal and vertical contours
  for (let i = 0; i < maxIndex; i++) {
    exportCombinedShapesForIndex(i, horizontalContours, verticalContours, modelDimensions);
  }
};

/**
 * Export both horizontal and vertical contours for a specific index to a single SVG file
 * 
 * @param {Number} index - The index to export
 * @param {Array} horizontalContours - Array of horizontal contour groups
 * @param {Array} verticalContours - Array of vertical contour groups
 * @param {Object} modelDimensions - Model dimensions
 */
const exportCombinedShapesForIndex = (index, horizontalContours, verticalContours, modelDimensions) => {
  console.log(`Exporting combined shapes for index ${index}`);
  
  const svgNamespace = "http://www.w3.org/2000/svg";
  
  // Create a new SVG element
  const svgElement = document.createElementNS(svgNamespace, "svg");
  
  // Set SVG attributes to match the original
  svgElement.setAttribute("width", "200mm"); 
  svgElement.setAttribute("height", "200mm");
  svgElement.setAttribute("viewBox", "0 0 200 200");
  
  // Add horizontal shape outlines (black)
  if (horizontalContours[index] && Array.isArray(horizontalContours[index])) {
    horizontalContours[index].forEach(shape => {
      if (!shape || !shape.points) return;
      
      const pathElement = document.createElementNS(svgNamespace, "path");
      
      // Create path data from points (using x and z for horizontal slices)
      let pathData = '';
      shape.points.forEach((point, i) => {
        if (i === 0) {
          pathData += `M ${point.x},${point.z || point.y}`;
        } else {
          pathData += ` L ${point.x},${point.z || point.y}`;
        }
      });
      pathData += ' Z'; // Close path
      
      pathElement.setAttribute("d", pathData);
      pathElement.setAttribute("stroke", "black");
      pathElement.setAttribute("fill", "none");
      
      svgElement.appendChild(pathElement);
    });
  }
  
  // Add horizontal slot lines (green)
  const xneg = horizontalContours.xneg || [];
  
  // Check if xneg is an array or an object with index keys
  if (xneg[index]) {
    // Check if xneg[index] is an array
    if (Array.isArray(xneg[index])) {
      xneg[index].forEach(line => {
        if (!line || !line.geometry) return;
        
        const pathElement = document.createElementNS(svgNamespace, "path");
        const positions = line.geometry.attributes.position;
        
        if (!positions) return;
        
        try {
          let pathData = `M ${positions.getX(0)},${positions.getZ(0)}`;
          for (let i = 1; i < positions.count; i++) {
            pathData += ` L ${positions.getX(i)},${positions.getZ(i)}`;
          }
          
          pathElement.setAttribute("d", pathData);
          pathElement.setAttribute("stroke", "green");
          pathElement.setAttribute("fill", "none");
          
          svgElement.appendChild(pathElement);
        } catch (error) {
          console.warn("Error creating path for line", error);
        }
      });
    } else if (xneg[index].children && Array.isArray(xneg[index].children)) {
      // Handle case where xneg[index] is an object with a children array
      xneg[index].children.forEach(line => {
        if (!line || !line.geometry) return;
        
        const pathElement = document.createElementNS(svgNamespace, "path");
        const positions = line.geometry.attributes.position;
        
        if (!positions) return;
        
        try {
          let pathData = `M ${positions.getX(0)},${positions.getZ(0)}`;
          for (let i = 1; i < positions.count; i++) {
            pathData += ` L ${positions.getX(i)},${positions.getZ(i)}`;
          }
          
          pathElement.setAttribute("d", pathData);
          pathElement.setAttribute("stroke", "green");
          pathElement.setAttribute("fill", "none");
          
          svgElement.appendChild(pathElement);
        } catch (error) {
          console.warn("Error creating path for line", error);
        }
      });
    } else {
      console.warn(`xneg[${index}] is neither an array nor has a children array`);
    }
  }
  
  // Add vertical shape outlines (black)
  if (verticalContours[index] && Array.isArray(verticalContours[index])) {
    verticalContours[index].forEach(shape => {
      if (!shape || !shape.points) return;
      
      const pathElement = document.createElementNS(svgNamespace, "path");
      
      // Create path data from points (using x and y for vertical slices)
      let pathData = '';
      shape.points.forEach((point, i) => {
        if (i === 0) {
          pathData += `M ${point.x},${point.y}`;
        } else {
          pathData += ` L ${point.x},${point.y}`;
        }
      });
      pathData += ' Z'; // Close path
      
      pathElement.setAttribute("d", pathData);
      pathElement.setAttribute("stroke", "black");
      pathElement.setAttribute("fill", "none");
      
      svgElement.appendChild(pathElement);
    });
  }
  
  // Add vertical slot lines (red) from xpositive
  const xpositive = verticalContours.xpositive || [];
  
  if (xpositive[index]) {
    // Handle different possible structures of xpositive[index]
    if (Array.isArray(xpositive[index])) {
      // Case 1: xpositive[index] is an array of groups
      xpositive[index].forEach(group => {
        if (!group) return;
        
        // Case 1a: group has a children array
        if (group.children && Array.isArray(group.children)) {
          group.children.forEach(line => {
            addSlotLine(line, svgElement, svgNamespace, "red");
          });
        } 
        // Case 1b: group is a line itself
        else if (group.geometry) {
          addSlotLine(group, svgElement, svgNamespace, "red");
        }
      });
    } 
    // Case 2: xpositive[index] is an object with a children array
    else if (xpositive[index].children && Array.isArray(xpositive[index].children)) {
      xpositive[index].children.forEach(line => {
        addSlotLine(line, svgElement, svgNamespace, "red");
      });
    }
    // Case 3: xpositive[index] is a line itself
    else if (xpositive[index].geometry) {
      addSlotLine(xpositive[index], svgElement, svgNamespace, "red");
    }
    else {
      console.warn(`xpositive[${index}] has an unexpected structure`, xpositive[index]);
    }
  }
  
  // Export the combined SVG
  const svgString = new XMLSerializer().serializeToString(svgElement);
  const blob = new Blob([svgString], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = `shape_${index + 1}.svg`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(url);
};

/**
 * Helper function to add a slot line to the SVG
 */
const addSlotLine = (line, svgElement, svgNamespace, color) => {
  if (!line || !line.geometry) return;
  
  const positions = line.geometry.attributes.position;
  if (!positions) return;
  
  try {
    const pathElement = document.createElementNS(svgNamespace, "path");
    
    if (typeof positions.getX !== 'function' || typeof positions.getY !== 'function') {
      console.warn("Position attribute missing getX/getY functions");
      return;
    }
    
    let pathData = `M ${positions.getX(0)},${positions.getY(0)}`;
    for (let i = 1; i < positions.count; i++) {
      pathData += ` L ${positions.getX(i)},${positions.getY(i)}`;
    }
    
    pathElement.setAttribute("d", pathData);
    pathElement.setAttribute("stroke", color);
    pathElement.setAttribute("fill", "none");
    
    svgElement.appendChild(pathElement);
  } catch (error) {
    console.warn("Error creating path for line", error);
  }
};

/**
 * Download SVG files directly - main entry point
 */
export const downloadSVG = (horizontalContours, verticalContours, modelDimensions, materialThickness) => {
  console.log("Download SVG called with:", { 
    horizontalContours, 
    verticalContours, 
    modelDimensions, 
    materialThickness 
  });
  
  // Call the generate function
  generateSVG(horizontalContours, verticalContours, modelDimensions, materialThickness);
};

/**
 * Helper function to prepare and structure the slot data for SVG export
 * @param {Object} horizontalContours - Horizontal contours object
 * @param {Array} xnegSlots - Array of negative X direction slots
 * @returns {Object} Updated horizontal contours with slot data
 */
export const prepareHorizontalSlotData = (horizontalContours, xnegSlots) => {
  if (!horizontalContours) {
    console.warn("Missing horizontalContours in prepareHorizontalSlotData");
    return {};
  }
  
  if (!xnegSlots) {
    console.warn("Missing xnegSlots in prepareHorizontalSlotData");
    return horizontalContours;
  }
  
  console.log("Preparing horizontal slot data with:", { horizontalContours, xnegSlots });
  
  // Make sure we have an object to work with
  const result = {...horizontalContours};
  
  // Attach xneg slots to horizontalContours for SVG export
  result.xneg = {};
  
  // Get numeric keys from horizontalContours
  const numericKeys = Object.keys(horizontalContours)
    .filter(key => !isNaN(parseInt(key)))
    .map(key => parseInt(key));
  
  // If xnegSlots is an array, convert it to an object with index keys
  if (Array.isArray(xnegSlots)) {
    // Create an entry for each index in horizontalContours
    numericKeys.forEach(index => {
      // Make an empty array for this index
      result.xneg[index] = [];
      
      // Add all items that have children property
      xnegSlots.forEach(item => {
        if (item) {
          result.xneg[index].push(item);
        }
      });
    });
  } else {
    // xnegSlots is already an object - check if it has numeric keys
    const existingKeys = Object.keys(xnegSlots)
      .filter(key => !isNaN(parseInt(key)))
      .map(key => parseInt(key));
    
    if (existingKeys.length > 0) {
      // Use existing structure
      existingKeys.forEach(key => {
        result.xneg[key] = xnegSlots[key];
      });
    } else {
      // No numeric keys, so distribute to all indices
      numericKeys.forEach(index => {
        result.xneg[index] = xnegSlots;
      });
    }
  }
  
  // Also attach lineGroups if they exist in xnegSlots structure
  if (xnegSlots.lineGroups) {
    result.lineGroups = xnegSlots.lineGroups;
  }
  
  return result;
};

/**
 * Helper function to prepare and structure the slot data for SVG export
 * @param {Object} verticalContours - Vertical contours object
 * @param {Array} xpositiveSlots - Array of positive X direction slots
 * @returns {Object} Updated vertical contours with slot data
 */
export const prepareVerticalSlotData = (verticalContours, xpositiveSlots) => {
  if (!verticalContours) {
    console.warn("Missing verticalContours in prepareVerticalSlotData");
    return {};
  }
  
  if (!xpositiveSlots) {
    console.warn("Missing xpositiveSlots in prepareVerticalSlotData");
    return verticalContours;
  }
  
  console.log("Preparing vertical slot data with:", { verticalContours, xpositiveSlots });
  
  // Make sure we have an object to work with
  const result = {...verticalContours};
  
  // Attach xpositive slots to verticalContours for SVG export
  result.xpositive = {};
  
  // Get numeric keys from verticalContours
  const numericKeys = Object.keys(verticalContours)
    .filter(key => !isNaN(parseInt(key)))
    .map(key => parseInt(key));
  
  // If xpositiveSlots is an array, convert it to an object with index keys
  if (Array.isArray(xpositiveSlots)) {
    // Create an entry for each index in verticalContours
    numericKeys.forEach(index => {
      // Make an empty array for this index
      result.xpositive[index] = [];
      
      // Add all items that have children property
      xpositiveSlots.forEach(item => {
        if (item) {
          result.xpositive[index].push(item);
        }
      });
    });
  } else {
    // xpositiveSlots is already an object - check if it has numeric keys
    const existingKeys = Object.keys(xpositiveSlots)
      .filter(key => !isNaN(parseInt(key)))
      .map(key => parseInt(key));
    
    if (existingKeys.length > 0) {
      // Use existing structure
      existingKeys.forEach(key => {
        result.xpositive[key] = xpositiveSlots[key];
      });
    } else {
      // No numeric keys, so distribute to all indices
      numericKeys.forEach(index => {
        result.xpositive[index] = xpositiveSlots;
      });
    }
  }
  
  return result;
};